// REST fetchers for the comms feature.
//
// The fetchers and the REST request/response shapes live in the shared
// package; the WS ClientFrame / ServerFrame / ChatStatus live beside the
// message model in its interfaces. Both are re-exported here so this file
// keeps owning the feature's wire shapes from the web's point of view (the
// runtime socket lives in Components/Pages/CommsPage/live/).

import { attachmentDownloadPath, createCommsApi } from '@d-orca/shared/client/apis';
import type { Attachment } from '@interfaces/ICommsApi';
import { authedFetch } from '@apis/auth';

export type {
  ChannelsResponse,
  HistoryResponse,
  MembersResponse,
  MessageReceiptsResponse,
  CreateChannelBody,
  CreateChannelResponse,
  UpdateChannelBody,
  UpdateChannelResponse,
  OpenDmResponse,
  AttachmentUploadResponse,
} from '@d-orca/shared/client/apis';
export type {
  ChannelLastMessagePreview,
  ChannelSummary,
  CommsMember,
  AttachmentRef,
  ClientFrame,
  ServerErrorCode,
  ServerFrame,
  ChatStatus,
} from '@interfaces/ICommsApi';
export { attachmentDownloadPath };

// The shared comms API bound to the web transport (cookie `authedFetch`);
// mobile binds the same factory to its Bearer transport. Exported whole for
// `clientApis.ts`, which hands it to the shared hooks via ClientApisProvider.
const api = createCommsApi(authedFetch);
export const commsApi = api;

export const {
  fetchChannels,
  fetchChannelHistory,
  fetchMembers,
  createChannel,
  openDm,
  deleteMessage,
  editMessage,
  markChannelRead,
  fetchMessageReceipts,
  deleteChannel,
  updateChannel,
  deleteAttachment,
} = api;

/**
 * Upload one file to a channel. Called at send-time. Builds the browser's
 * multipart form for the shared fetcher; `durationMs` is required for voice
 * notes only.
 */
export function uploadAttachment(
  siteId: string,
  channelId: string,
  file: Blob,
  fileName: string,
  durationMs?: number,
): Promise<Attachment> {
  const form = new FormData();
  // durationMs must precede the file: the backend streams the file part straight
  // to storage as it arrives, so any metadata fields have to be read first.
  if (durationMs != null) form.append('durationMs', String(Math.floor(durationMs)));
  form.append('file', file, fileName);
  return api.uploadAttachment(siteId, channelId, form);
}
